<script lang="ts">
  import { IndianRupee, ShoppingBag, Package, AlertTriangle } from '@lucide/svelte';
  import { StatCard } from '@aatmanova/ui';
  import SalesForecastChart from './SalesForecastChart.svelte';
  import OrderHotspotMap from './OrderHotspotMap.svelte';
  import SystemHealthPanel from './SystemHealthPanel.svelte';

  interface SalesPoint {
    label: string;
    actual: number;
    forecast: number;
  }

  interface Hotspot {
    region: string;
    x: number;
    y: number;
    ordersCount: number;
    popularCategory: string;
  }

  interface HealthData {
    database: { status: string; sizeMb: string; totalOrders: number; totalProducts: number };
    redis: { status: string; memoryUsed: string | null };
    uptime: number;
  }

  interface Props {
    stats: {
      totalRevenue: number;
      totalOrdersCount: number;
      totalProductsCount: number;
      lowStockCount: number;
    };
    recentActivities: Array<{
      type: string;
      title: string;
      desc: string;
      time: string;
    }>;
    salesData?: SalesPoint[];
    monthlyGoal?: number;
    hotspots?: Hotspot[];
    healthData?: HealthData | null;
  }

  let {
    stats,
    recentActivities,
    salesData = [],
    monthlyGoal = 500000,
    hotspots = [],
    healthData = null
  }: Props = $props();
</script>

<div class="space-y-8">
  <div class="space-y-1">
    <h2 class="text-3xl font-extrabold tracking-tight text-base-content">Control Overview</h2>
    <p class="text-sm text-base-content/70">Live operational telemetry and data distribution metrics</p>
  </div>

  <!-- Bento Metric Grid -->
  <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
    <StatCard
      title="Gross Sales"
      value="Rs. {(stats.totalRevenue || 0).toFixed(2)}"
      desc="Aggregated order values"
      iconColor="text-success"
    >
      {#snippet icon()}
        <IndianRupee class="w-6 h-6" />
      {/snippet}
    </StatCard>

    <StatCard
      title="Orders Processed"
      value={stats.totalOrdersCount}
      desc="Transaction entries volume"
      iconColor="text-info"
    >
      {#snippet icon()}
        <ShoppingBag class="w-6 h-6" />
      {/snippet}
    </StatCard>

    <StatCard
      title="Products Catalog"
      value={stats.totalProductsCount}
      desc="Spiritual objects configured"
      iconColor="text-warning"
    >
      {#snippet icon()}
        <Package class="w-6 h-6" />
      {/snippet}
    </StatCard>

    <StatCard
      title="Stock Alerts"
      value={stats.lowStockCount}
      desc="Nodes with low stock warnings"
      iconColor="text-error"
      class={stats.lowStockCount > 0 ? 'text-error animate-pulse' : ''}
    >
      {#snippet icon()}
        <AlertTriangle class="w-6 h-6" />
      {/snippet}
    </StatCard>
  </div>

  <!-- Lower Visual Grid -->
  <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
    <!-- Sales & Forecast Chart -->
    <div class="lg:col-span-2">
      <SalesForecastChart {salesData} {monthlyGoal} />
    </div>

    <!-- System Audit Feed -->
    <div class="p-6 bg-base-100 border border-base-300 rounded-2xl space-y-6">
      <h3 class="text-sm font-bold uppercase tracking-wider text-base-content/70 flex items-center justify-between">
        Operational Logs
        <span class="flex h-2 w-2 rounded-full bg-success animate-ping"></span>
      </h3>
      <div class="space-y-4">
        {#each recentActivities as act}
          <div class="flex gap-3 text-xs leading-relaxed">
            <div class="w-7 h-7 rounded-lg border border-base-300 bg-base-200 flex items-center justify-center shrink-0">
              {#if act.type === 'order'}
                <ShoppingBag class="w-4 h-4 text-success" />
              {:else}
                <AlertTriangle class="w-4 h-4 text-error" />
              {/if}
            </div>
            <div class="min-w-0 flex-1">
              <p class="font-bold text-base-content truncate">{act.title}</p>
              <p class="text-base-content/70 text-[0.6875rem] leading-snug">{act.desc}</p>
            </div>
          </div>
        {/each}
        {#if recentActivities.length === 0}
          <p class="text-xs text-base-content/40 py-12 text-center uppercase tracking-widest">Logs currently empty</p>
        {/if}
      </div>
    </div>
  </div>

  <!-- Second Row: Hotspot Map + System Health -->
  <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
    <div class="lg:col-span-2">
      <OrderHotspotMap {hotspots} />
    </div>
    <div>
      <SystemHealthPanel {healthData} />
    </div>
  </div>
</div>
